import { Fire, Star } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import { ExpensesSectionLabel } from '../expenses/ExpensesSectionLabel';

/**
 * `.pen` → `ds/tile/month` (master `TB9HY`). Grading confirmed per-instance in
 * `Calendário Anual · Populado · {375,768,1280} light/dark`:
 * - vazio: `border-border bg-card`, texto `text-muted-foreground`.
 * - sonho (sem/pouco dever): `border-sage-border bg-sage-tint`, texto `text-sage-on-tint`.
 * - dever leve (intensidade 1): `bg-warning-tint`, abbr `text-warning-fg`, valor
 *   fica `text-foreground` (NÃO colorido) — sem fogo.
 * - dever médio (intensidade 2): `bg-warning-tint border-warning-500`, abbr+valor
 *   `text-warning-fg`, ícone fogo.
 * - pico (intensidade 3): `bg-warning-700` sólido, todo o texto/ícones `text-warning-50`.
 * O anel de selecção (`border-sage-600` 2px + `shadow-sage-glow`) é uma camada
 * independente da intensidade — aplica-se por cima de qualquer estado.
 */
function monthToneClasses(meta) {
  const { hasDream, intensity } = meta;
  if (hasDream && intensity < 2) {
    return { wrap: 'border-sage-border bg-sage-tint', text: 'text-sage-on-tint' };
  }
  if (intensity >= 3) {
    return { wrap: 'border-border bg-warning-700', text: 'text-warning-50' };
  }
  if (intensity === 2) {
    return { wrap: 'border-warning-500 bg-warning-tint', text: 'text-warning-fg' };
  }
  if (intensity === 1) {
    return { wrap: 'border-border bg-warning-tint', text: 'text-warning-fg' };
  }
  return { wrap: 'border-border bg-card', text: 'text-muted-foreground' };
}

function monthAmountClasses(meta, showAmount) {
  const { hasDream, intensity } = meta;
  if (hasDream && intensity < 2) return 'text-sage-on-tint';
  if (!showAmount) return 'text-muted-foreground';
  if (intensity >= 3) return 'text-warning-50';
  if (intensity >= 2) return 'text-warning-fg';
  if (intensity === 1) return 'text-foreground';
  return 'text-muted-foreground';
}

export function AnnualYearGrid({
  monthsMeta = [],
  selectedMonth,
  onSelectMonth,
  gridRef,
}) {
  const hasAnyDuty = monthsMeta.some((meta) => meta.dutyTotal > 0);

  return (
    <section ref={gridRef} className="flex flex-col gap-space-3">
      {hasAnyDuty ? (
        <div className="flex items-center gap-space-2 px-0.5 type-caption text-muted-foreground">
          <span>{pt.calendar.yearGrid.scaleLess}</span>
          <span
            className="h-2 flex-1 rounded-ds-full border border-border bg-gradient-to-r from-warning-50 via-warning-500 to-warning-700"
            aria-hidden
          />
          <span>{pt.calendar.yearGrid.scalePeak}</span>
        </div>
      ) : null}

      <ExpensesSectionLabel title={pt.calendar.yearGrid.eyebrow} />

      <div
        className="grid grid-cols-3 gap-space-2 md:grid-cols-4 lg:grid-cols-6"
        role="grid"
        aria-label={pt.calendar.gridLabel}
        data-testid="annual-year-grid"
      >
        {monthsMeta.map((meta) => {
          const isSelected = meta.month === selectedMonth;
          const showAmount = meta.dutyTotal > 0;
          const showFlame = meta.intensity >= 2;
          const tone = monthToneClasses(meta);

          return (
            <button
              key={meta.month}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${meta.shortLabel}${showAmount ? ` ${formatEUR(meta.dutyTotal)}` : ''}${meta.hasDream ? ` · ${pt.calendar.yearGrid.legendDream}` : ''}`}
              onClick={() => onSelectMonth?.(meta.month)}
              className={cn(
                'flex h-[74px] flex-col justify-between rounded-ds-md border p-space-2-5 text-left transition-shadow md:p-space-2',
                tone.wrap,
                isSelected && 'border-2 border-sage-600 shadow-[0_2px_12px] shadow-sage-glow',
              )}
            >
              <div className="flex items-center justify-between gap-space-1">
                <span className={cn('type-chip font-bold tracking-[0.03em]', tone.text)}>
                  {meta.shortLabel}
                </span>
                <span className="flex shrink-0 items-center gap-0.5">
                  {showFlame ? <Fire className={cn('h-3.5 w-3.5', tone.text)} aria-hidden /> : null}
                  {meta.hasDream ? (
                    <Star
                      className={cn('h-3 w-3', meta.intensity >= 3 ? 'text-warning-50' : 'text-sage-on-tint')}
                      aria-hidden
                    />
                  ) : null}
                </span>
              </div>
              <span className={cn('type-display-row', monthAmountClasses(meta, showAmount))}>
                {showAmount ? formatEUR(meta.dutyTotal) : '—'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-space-4 px-1 type-caption text-muted-foreground">
        <span className="inline-flex items-center gap-space-2">
          <i className="inline-flex h-[15px] w-[15px] items-center justify-center rounded-ds-sm bg-warning-tint text-warning-fg">
            <Fire className="h-2 w-2" aria-hidden />
          </i>
          {pt.calendar.yearGrid.legendHeavy}
        </span>
        <span className="inline-flex items-center gap-space-2">
          <i className="inline-flex h-[15px] w-[15px] items-center justify-center rounded-ds-sm bg-sage-tint text-sage-on-tint">
            <Star className="h-2 w-2" aria-hidden />
          </i>
          {pt.calendar.yearGrid.legendDream}
        </span>
      </div>
    </section>
  );
}
